import { useState } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  useAdminUsers,
  invalidateAdminUsers,
  useAlert,
  useApi,
} from "../../hooks";
import { useLoaderStore } from "../../context";
import { API } from "../../utils/constants";
import { ActiveToggle, TextInput } from "../../components/Inputs";

// ── User add/edit modal ─────────────────────────────────────────────────────

/**
 * UserModal — Create or edit an admin user (SPR-008 FE-2).
 *
 * Create (POST /api/admin/users): username, firstName, lastName, email — NO
 * password (the backend emails a single-use invite/set-password link).
 * Edit (PUT /api/admin/users/{id}): the same fields + isActive.
 *
 * A 409 (duplicate username/email) is surfaced as an inline field error and a
 * toast. Follows the SPR-007 modal discipline (blank create / prefilled edit /
 * cleared on success) — the parent remounts the modal per open via a key.
 */
function UserModal({ open, onClose, user = null }) {
  const isEdit = !!user;
  const alert = useAlert();
  const { post, put } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [username, setUsername] = useState(user?.username ?? "");
  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  // Inline conflict error (409) keyed to the offending field.
  const [conflictError, setConflictError] = useState("");

  const validate = () => {
    if (!username.trim()) { alert("Validation", "Username is required.", "warning"); return false; }
    if (!firstName.trim()) { alert("Validation", "First name is required.", "warning"); return false; }
    if (!lastName.trim()) { alert("Validation", "Last name is required.", "warning"); return false; }
    if (!email.trim()) { alert("Validation", "Email is required.", "warning"); return false; }
    return true;
  };

  const handleSubmit = async () => {
    setConflictError("");
    if (!validate()) return;
    setShowLoader(true);

    try {
      if (isEdit) {
        await put(API.ADMIN.USERS.UPDATE(user.id), {
          username: username.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          isActive,
        });
        alert("Updated", `"${username}" updated successfully.`, "success");
      } else {
        // No password — the backend emails an invite (set-password) link.
        await post(API.ADMIN.USERS.CREATE, {
          username: username.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
        });
        alert("Invite sent", `An invite was emailed to "${email}".`, "success");
      }
      await invalidateAdminUsers();
      onClose();
    } catch (err) {
      if (err?.status === 409) {
        const msg = err?.data?.detail || "That username or email is already in use.";
        setConflictError(msg);
        alert("Conflict", msg, "error");
      } else {
        alert("Error", err?.data?.detail || "Failed to save user.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
                data-testid="user-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title className="font-display text-2xl text-charcoal uppercase">
                    {isEdit ? "EDIT USER" : "ADD USER"}
                  </Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal" className="text-charcoal/40 hover:text-charcoal">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <TextInput
                    label="Username *"
                    name="user-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                  <TextInput
                    label="First Name *"
                    name="user-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <TextInput
                    label="Last Name *"
                    name="user-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  <TextInput
                    label="Email *"
                    name="user-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />

                  {isEdit && (
                    <div className="flex items-center gap-3">
                      <ActiveToggle
                        enabled={isActive}
                        onToggle={() => setIsActive((v) => !v)}
                        disabled={!!user?.isProtected}
                        label="Toggle user active"
                        testId="user-modal-active-toggle"
                      />
                      <span className="font-body text-sm text-charcoal/70">
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  )}

                  {!isEdit && (
                    <p className="font-body text-xs text-charcoal/50">
                      No password is set here — an invite link is emailed to the
                      new user so they can set their own.
                    </p>
                  )}

                  {conflictError && (
                    <p className="font-body text-sm text-rust" data-testid="user-conflict-error">
                      {conflictError}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                    data-testid="user-modal-submit-button"
                  >
                    {isEdit ? "SAVE CHANGES" : "SEND INVITE"}
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Delete confirmation ─────────────────────────────────────────────────────

function DeleteUserDialog({ open, onClose, onConfirm, username }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-charcoal/50" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-sm bg-white rounded-2xl border-2 border-charcoal shadow-[6px_6px_0_rgba(43,39,36,0.18)] p-6"
              data-testid="delete-user-dialog"
            >
              <Dialog.Title className="font-display text-2xl text-charcoal uppercase mb-3">
                DELETE USER
              </Dialog.Title>
              <p className="font-body text-charcoal/70 mb-6">
                Delete <strong>"{username}"</strong>? This is permanent and cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onConfirm}
                  className="flex-1 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
                  data-testid="confirm-delete-user-button"
                >
                  DELETE
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 border-2 border-teal text-teal font-heading tracking-wider py-2 rounded-xl hover:bg-teal hover:text-white transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ── Users admin page ────────────────────────────────────────────────────────

/**
 * Users — Admin user management (SPR-007 lifecycle + SPR-008 add/edit).
 *
 * Lists users with an Active toggle (PUT isActive → DeactivatedAt) and a
 * permanent delete (tombstone). SPR-008 adds create (email invite — no
 * password) and full edit modals; duplicate username/email surfaces as a 409.
 * A protected user (isProtected — the seeded main admin) cannot be deactivated
 * or deleted (CON-002 §9A/§9B).
 */
export default function Users() {
  const alert = useAlert();
  const { put, del } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { users, isLoading, error } = useAdminUsers();

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  // Bumped on every open so the modal remounts with fresh state — guarantees
  // blank-on-create, prefilled-on-edit, and cleared-on-success (FE-2).
  const [modalSession, setModalSession] = useState(0);

  const openCreate = () => { setEditUser(null); setModalSession((n) => n + 1); setModalOpen(true); };
  const openEdit = (user) => { setEditUser(user); setModalSession((n) => n + 1); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditUser(null); };

  const handleToggleActive = async (user) => {
    if (user.isProtected) {
      alert("Protected", "The main admin cannot be deactivated.", "warning");
      return;
    }
    setShowLoader(true);
    try {
      await put(API.ADMIN.USERS.UPDATE(user.id), { isActive: !user.isActive });
      await invalidateAdminUsers();
      alert("Updated", `"${user.username}" ${!user.isActive ? "activated" : "deactivated"}.`, "success");
    } catch (err) {
      if (err?.status === 409) {
        alert("Conflict", err?.data?.detail || "This user is protected and cannot be deactivated.", "error");
      } else {
        alert("Error", err?.data?.detail || "Failed to update user.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteTarget.isProtected) return;
    setShowLoader(true);
    try {
      await del(API.ADMIN.USERS.DELETE(deleteTarget.id));
      await invalidateAdminUsers();
      alert("Deleted", `"${deleteTarget.username}" permanently deleted.`, "success");
      setDeleteTarget(null);
    } catch (err) {
      if (err?.status === 409) {
        alert("Protected", err?.data?.detail || "This user is protected and cannot be deleted.", "error");
      } else {
        alert("Error", err?.data?.detail || "Failed to delete user.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <main data-testid="admin-users-page" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-charcoal uppercase">User Management</h1>
          <p className="font-body text-charcoal/60 text-sm mt-1">
            Invite teammates by email, edit details, deactivate or delete — the main admin is protected.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-rust text-white font-heading tracking-widest px-5 py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
          data-testid="add-user-button"
        >
          + ADD USER
        </button>
      </div>

      {isLoading && <p className="font-body text-charcoal/60">Loading users…</p>}
      {error && <p className="font-body text-rust">Unable to load users.</p>}

      {!isLoading && !error && (
        <div className="overflow-x-auto">
          <table className="w-full border-2 border-charcoal rounded-2xl overflow-hidden">
            <thead className="bg-charcoal text-white">
              <tr>
                {["Username", "Active", "Actions"].map((h) => (
                  <th key={h} className="font-heading tracking-wider text-left px-4 py-3 text-sm">
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-10 font-body text-charcoal/50">
                    No users found.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-aluminum/40 hover:bg-cream/50 transition-colors"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-4 py-3 font-body text-charcoal">
                    {u.username}
                    {u.isProtected && (
                      <span
                        className="ml-2 align-middle font-heading tracking-wider text-[10px] bg-mustard text-charcoal px-2 py-0.5 rounded-full"
                        data-testid={`protected-badge-${u.id}`}
                      >
                        PROTECTED
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveToggle
                      enabled={u.isActive}
                      onToggle={() => handleToggleActive(u)}
                      disabled={!!u.isProtected}
                      label={`Toggle ${u.username} active`}
                      testId={`toggle-active-${u.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(u)}
                        className="font-heading tracking-wider text-xs text-teal border-2 border-teal px-3 py-1 rounded-full hover:bg-teal hover:text-white transition-colors"
                        data-testid={`edit-user-${u.id}`}
                      >
                        EDIT
                      </button>
                      {/* Protected users hide the delete control entirely. */}
                      {u.isProtected ? (
                        <span className="font-body text-charcoal/40 text-xs italic self-center">
                          Protected
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeleteTarget(u)}
                          className="font-heading tracking-wider text-xs text-rust border-2 border-rust px-3 py-1 rounded-full hover:bg-rust hover:text-white transition-colors"
                          data-testid={`delete-user-${u.id}`}
                        >
                          DELETE
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal — keyed so each open starts from fresh state */}
      <UserModal
        key={`${editUser ? `edit-${editUser.id}` : "create"}-${modalSession}`}
        open={modalOpen}
        onClose={closeModal}
        user={editUser}
      />

      <DeleteUserDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        username={deleteTarget?.username ?? ""}
      />
    </main>
  );
}
